package com.sddamico.swigmore.ds.core

import androidx.compose.runtime.Composable
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeMono
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Ballot submission states, as tagged in the commissioner's table. */
enum class SubmissionStatus(val label: String) {
  OnTime("On time"), Amended("Amended"), Withdrawn("Withdrawn"), Late("Late")
}

/** Small rounded status pill (ON TIME / AMENDED / WITHDRAWN / LATE). */
@Composable
fun StatusTag(status: SubmissionStatus, modifier: Modifier = Modifier) {
  val (bg, fg) = when (status) {
    SubmissionStatus.OnTime -> SwColor.field700 to SwColor.cream
    SubmissionStatus.Amended, SubmissionStatus.Late -> SwColor.gold to SwColor.field700
    SubmissionStatus.Withdrawn -> SwColor.tintOnCreamStrong to SwColor.field700
  }
  Span(
    attrs = Modifier.typeCaps("8px", ".12em")
      .css("display" to "inline-block", "padding" to "2px 7px", "border-radius" to "10px",
        "background" to bg, "color" to fg)
      .then(modifier)
      .toAttrs()
  ) { Text(status.label) }
}

/** Mono outlined status chip with a gold dot: "● FANTRAX SYNCED 4M AGO". */
@Composable
fun SyncBadge(text: String, modifier: Modifier = Modifier, ok: Boolean = true) {
  Span(
    attrs = Modifier.typeMono()
      .css("color" to "rgba(239,229,201,.65)", "padding" to "5px 8px", "text-transform" to "uppercase",
        "border" to "1px solid ${SwColor.borderOnField}", "border-radius" to SwEffects.radiusXs,
        "white-space" to "nowrap")
      .then(modifier)
      .toAttrs()
  ) {
    Span(attrs = Modifier.css("color" to if (ok) SwColor.gold else SwColor.textFaint).toAttrs()) { Text("● ") }
    Text(text)
  }
}

/** Round initials avatar (header account button). */
@Composable
fun Avatar(initials: String, modifier: Modifier = Modifier, size: Int = 34, onClick: (() -> Unit)? = null) {
  val handler = onClick
  Div(
    attrs = Modifier
      .typeBody("${size * 12 / 34}px", 800, "1")
      .css("width" to "${size}px", "height" to "${size}px", "border-radius" to "50%",
        "background" to SwColor.cream, "color" to SwColor.field700, "display" to "flex",
        "align-items" to "center", "justify-content" to "center", "flex" to "0 0 auto")
      .then(if (onClick != null) Modifier.css("cursor" to "pointer") else Modifier)
      .then(modifier)
      .toAttrs {
        if (handler != null) {
          attr("role", "button")
          attr("tabindex", "0")
          onClick { handler() }
          onKeyDown { if (it.key == "Enter" || it.key == " ") { it.preventDefault(); handler() } }
        }
      }
  ) { Text(initials.take(2).uppercase()) }
}
